
import { defineComponent, inject, PropType, provide, reactive, watch } from "vue";
import { NotifyKey } from "../types/notify";
import Validator, { ValidationRules } from "../types/Validator";
import {
  FormValidatedValuesKey,
  FormValidatedUpdateKey,
  FormValidatedErrorsKey,
} from "../types/inject";
import { ObjetoPadrao } from "../types/objetopadrao";
import router from "@/router";
import DialogModal from "./Modal/DialogModal.vue";
import helpers from "@/helpers";
import { getThemeColor } from "@/branding";

export default defineComponent({
  props: {
    initialValues: {
      type: Object,
      required: true,
    },
    submitFixed: {
      type: Boolean,
      default: () => true,
    },
    hideButtonBack: {
      type: Boolean,
      default: () => false,
    },
    hideButtons: {
      type: Boolean,
      default: () => false,
    },
    validation: Object as PropType<ValidationRules>,
    /**
     * Validações que dependem de mais de um campo/estado externo. Recebe os
     * values e retorna { campo: mensagem }; roda junto com as declarativas no
     * submit, para todos os campos obrigatórios aparecerem de uma só vez.
     */
    validacaoExtra: Function as PropType<
      (values: ObjetoPadrao) => Record<string, string>
    >,
    isRemoving: Boolean,
    error: String,
  },
  components: {
    DialogModal,
  },
  setup(props, { emit }) {
    const validator = new Validator();
    const theme = getThemeColor();
    const values = reactive({ ...props.initialValues } as ObjetoPadrao);
    const errors = reactive({} as Record<string, string>);

    provide(FormValidatedValuesKey, values);
    provide(FormValidatedErrorsKey, errors);

    const onSubmit = () => {
      // Clear errors
      for (const k in errors) {
        delete errors[k];
      }
      // Revalidate all data
      validateObject(values as ObjetoPadrao);

      if (typeof props.validacaoExtra === "function") {
        const extras = props.validacaoExtra(values as ObjetoPadrao) ?? {};
        for (const k in extras) {
          if (extras[k]) {
            errors[k] = extras[k];
          }
        }
      }

      const nerrors = Object.keys(errors).filter((k) => !!errors[k]).length;
      if (nerrors > 0) {
        helpers.mensagemErro(`Corrija os erros e tente novamente!`);
        return;
      }
      emit("save", values);
    };

    const onUpdate = (val: ObjetoPadrao) => {
      for (const k in val) {
        values[k] = val[k];

        // Campo sem regra declarativa: limpa erro setado manualmente
        // (via setErrors) assim que o usuário altera o valor.
        if (!props.validation || !props.validation[k]) {
          errors[k] = "";
        }
      }
      validateObject(val);
    };
    provide(FormValidatedUpdateKey, onUpdate);

    /**
     * Permite ao componente pai marcar erros de validações imperativas
     * (feitas no handler de save) nos campos, com o mesmo visual das
     * validações declarativas.
     */
    const setErrors = (novos: Record<string, string>) => {
      for (const k in novos) {
        errors[k] = novos[k];
      }
    };

    const validateObject = (val: ObjetoPadrao) => {
      if (!props.validation) {
        return;
      }
      for (const k in val) {
        if (props.validation[k] && typeof props.validation[k] === "string") {
          // Existe regra para o campo em questao
          // Realizar validacao do item e gravar o status da validacao
          const errMsg = validator.validate(val[k], props.validation[k]);
          errors[k] = errMsg;
        }
      }
    };

    // Busca novas mudanças e aplica nos valores;
    watch(
      () => props.initialValues,
      (newVal) => {
        resetForm(newVal);
      }
    );

    const resetForm = (newVal: ObjetoPadrao) => {
      // Apaga todas as chaves no objeto
      for (const k in values) {
        delete values[k];
      }
      // Cria todas as chaves com os novos valores
      for (const k in newVal) {
        values[k] = JSON.parse(JSON.stringify(newVal[k]));
      }
    };

    /**
     * Volta para lista
     */
    const back = () => {
      router.back();
    };

    return {
      theme,
      onSubmit,
      onUpdate,
      resetForm,
      setErrors,
      values,
      back,
      remover: () => emit("remover", props.initialValues),
      closeError: () => emit("closeError"),
    };
  },
});
